import type { CityConfig } from "@/pages/city-template/types";

export const warwickConfig: CityConfig = {
  city: "Warwick",
  state: "Rhode Island",
  stateAbbr: "RI",
  slug: "warwick",
  route: "/cities/warwick",
  title: "Expedited Passport Services in Warwick, RI | Fast Passport Center",
  metaDescription:
    "Get your passport fast in Warwick, RI. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered State Department courier serving Warwick, T.F. Green Airport, and all of Rhode Island.",
  h1: "Expedited Passport Services in Warwick, RI",
  heroSubtitle:
    "Home to T.F. Green Airport and Rhode Island's second-largest city. Fast Passport Center delivers emergency and expedited passports as fast as 24 hours for Warwick travelers.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Aerial%20view%20of%20T.F.%20Green%20Airport%20in%20Warwick%20Rhode%20Island%20with%20modern%20terminal%20buildings%20and%20runway%2C%20coastal%20New%20England%20scenery%20with%20green%20landscape%2C%20warm%20golden%20afternoon%20light%2C%20professional%20aviation%20photography%2C%20clean%20composition%20with%20blue%20sky%2C%20no%20text%20overlay&width=1600&height=700&seq=ri-war-hero-001&orientation=landscape",
  heroImageAlt: "T.F. Green Airport in Warwick Rhode Island aerial view",
  geoPosition: "41.7001; -71.4162",
  geoPlacename: "Warwick, Rhode Island, United States",
  lastModified: "2026-05-31",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Thomas P. O'Neill Jr. Federal Building, 10 Causeway Street, Boston, MA 02222",
    distance: "~50 miles from Warwick",
  },
  nearbyCitiesZips: [
    { name: "Warwick", zips: "02886-02889, 02818" },
    { name: "Providence", zips: "02901-02912, 02918, 02940" },
    { name: "Cranston", zips: "02905-02907, 02910, 02920-02921" },
    { name: "East Greenwich", zips: "02818" },
    { name: "North Kingstown", zips: "02852, 02874" },
    { name: "Johnston", zips: "02919" },
    { name: "West Warwick", zips: "02893" },
    { name: "Coventry", zips: "02816, 02827" },
    { name: "Pawtucket", zips: "02860-02862" },
    { name: "East Providence", zips: "02914-02916" },
    { name: "North Providence", zips: "02904, 02911" },
    { name: "Barrington", zips: "02806" },
    { name: "Bristol", zips: "02809" },
    { name: "Narragansett", zips: "02882" },
    { name: "Newport", zips: "02840-02841" },
    { name: "Woonsocket", zips: "02895" },
    { name: "Cumberland", zips: "02864, 02827" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for Warwick travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving All of Warwick",
      desc: "Apponaug, Conimicut, Cowesett, Hoxsie, Kent, Norwood, Pilgrim — we cover every Warwick neighborhood.",
    },
  ],
  testimonials: [
    {
      name: "Michael D.",
      location: "Warwick, RI",
      rating: 5,
      text: "Flying out of T.F. Green in 6 days and realized my passport was expired. Fast Passport Center saved my trip. Had it back in 4 days.",
      service: "Emergency Passport",
    },
    {
      name: "Jennifer K.",
      location: "East Greenwich, RI",
      rating: 5,
      text: "Applied for my son's first passport through Fast Passport Center. They explained the two-parent rule clearly and made the process smooth.",
      service: "Child Passport",
    },
    {
      name: "Robert H.",
      location: "West Warwick, RI",
      rating: 5,
      text: "Renewed my passport in under a week. The document review caught a small error I would have missed. Great attention to detail.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "war-1",
      name: "Warwick Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "575 Sandy Lane",
      city: "Warwick",
      state: "RI",
      zip: "02889",
      phone: "401-737-8700",
      hours: "Mon-Fri 8:00 AM - 4:30 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Central Warwick",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "war-2",
      name: "Kent County Clerk",
      type: "County Clerk / Passport Services",
      address: "222 Quaker Lane",
      city: "Warwick",
      state: "RI",
      zip: "02886",
      phone: "401-738-6400",
      hours: "Mon-Fri 8:30 AM - 4:30 PM",
      distance: "West Warwick",
      website: "https://www.ri.gov/",
      websiteLabel: "State of Rhode Island",
      notes: "Provides passport application acceptance services. Appointment required for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "war-3",
      name: "Apponaug Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "33 South Main Street",
      city: "Warwick",
      state: "RI",
      zip: "02886",
      phone: "401-737-3330",
      hours: "Mon-Fri 8:00 AM - 4:30 PM",
      distance: "Apponaug Village",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. Photo services available on-site.",
      icon: "ri-building-2-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Warwick, RI?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the Boston Passport Agency if I live in Warwick?",
      a: "No — if you use Fast Passport Center, you don't need to visit the Boston Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the Boston agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in Warwick?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout Warwick. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from Warwick?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in Warwick?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in Warwick?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Warwick. We serve Warwick and all of Rhode Island through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Warwick", address: "various CVS locations in Warwick", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Warwick", address: "various Walgreens locations in Warwick", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Warwick", address: "various UPS Store locations in Warwick", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Warwick", address: "various FedEx Office locations in Warwick", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Warwick", address: "various Walmart locations in Warwick", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "RI DMV — Cranston",
      address: "600 New London Ave, Cranston, RI 02920",
      hours: "Mon-Fri 8:30 AM - 3:15 PM",
      note: "Main Rhode Island DMV office. Accepts walk-ins but appointments are strongly recommended.",
      featured: true,
    },
    {
      name: "RI DMV Express — Pawtucket",
      address: "1250 Elmwood Ave, Cranston, RI 02910",
      hours: "Mon-Fri 8:30 AM - 3:15 PM",
      note: "Express DMV services for license renewals and REAL ID applications.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PVD",
      name: "T.F. Green Airport",
      distance: "Located in Warwick",
      address: "2000 Post Rd, Warwick, RI 02886",
      note: "Rhode Island's primary airport, located right in Warwick. Serves domestic destinations and limited international connections. Perfect for Warwick travelers.",
      flightUrl: "https://www.pvdairport.com/",
      flightLabel: "T.F. Green Airport",
      featured: true,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "~50 miles from Warwick",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with direct flights to Europe, Asia, and worldwide destinations. Warwick travelers often use Logan for international travel.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan International",
      featured: false,
    },
    {
      code: "BDL",
      name: "Bradley International Airport",
      distance: "~70 miles from Warwick",
      address: "Schoephoester Rd, Windsor Locks, CT 06096",
      note: "Connecticut's major airport with domestic and some international service. Alternative option for Warwick-area travelers.",
      flightUrl: "https://www.bradleyairport.com/",
      flightLabel: "Bradley International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Warwick travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Warwick residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Warwick residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Warwick residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Warwick, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Warwick residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Warwick travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Warwick residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Warwick residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Warwick residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Warwick." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Warwick." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Warwick travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Warwick travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Warwick-area team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Warwick address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: [
        "Mail-in application",
        "No expedited handling",
        "No dedicated agent",
        "No phone or email contact for your case",
        "Updates only by physical mail letter",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: [
        "For documented life-or-death emergencies only",
        "Requires proof of emergency travel",
        "Strictly for documented life-or-death emergencies",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Providence", route: "/cities/providence" },
    { name: "Cranston", route: "/cities/cranston" },
    { name: "Pawtucket", route: "/cities/pawtucket" },
    { name: "Newport", route: "/cities/newport-ri" },
    { name: "East Greenwich", route: "/states/rhode-island" },
    { name: "North Kingstown", route: "/states/rhode-island" },
    { name: "Johnston", route: "/cities/johnston" },
    { name: "West Warwick", route: "/cities/west-warwick" },
    { name: "Coventry", route: "/cities/coventry" },
    { name: "East Providence", route: "/cities/east-providence" },
  ],
  fpcOfficeNote:
    "Fast Passport Center serves Warwick and all of Rhode Island as a remote courier service. We do not have a physical office in Warwick, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeDistance: "Remote courier service — Warwick, RI area",
  serviceAreaIntro:
    "Fast Passport Center provides expedited passport services to all Warwick neighborhoods and surrounding Rhode Island communities. From Apponaug to Conimicut, Cowesett to Hoxsie, Kent to Norwood — we serve every Warwick neighborhood with the same fast, reliable processing.",
  nearbyCitiesIntro:
    "We proudly serve Warwick and all surrounding Rhode Island communities. Whether you're near T.F. Green Airport or in a nearby suburb, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro:
    "Our Warwick customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro:
    "These are the most common mistakes Warwick travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro:
    "Warwick and Rhode Island travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro:
    "Common questions about expedited passport services in Warwick, RI. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/rhode-island",
  statePageName: "Rhode Island Passport Services",
  dmvWebsite: "https://dmv.ri.gov",
  dmvLabel: "Rhode Island DMV — Official Website",
};

export default warwickConfig;